package org.moxultimate.topwithmox.wallpaper.ui

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.image.WallpaperDisplay
import org.moxultimate.topwithmox.components.image.wallpaperAspectRatio
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.PageHeading
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.statics.WallpaperStatics
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.MaxChipFontSize
import org.moxultimate.topwithmox.theme.MinChipFontSize
import org.moxultimate.topwithmox.theme.WhiteBG
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse

@Composable
fun WallpaperPage() {
    val viewModel: WallpaperViewModel = koinViewModel()
    val screenState by viewModel.screenState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val allSeries by viewModel.allSeries.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val searchText by viewModel.searchText.collectAsState()
    val hasMoreInCurrentView by viewModel.hasMoreInCurrentView.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val selectedSeries by viewModel.selectedSeries.collectAsState()
    val only360 by viewModel.only360.collectAsState()

    // Retain the last successful result so the wallpaper grid stays visible while
    // a filter chip reload is in flight. Without this the whole page would flicker
    // to a blank loading screen on every filter change.
    var lastSuccess by remember { mutableStateOf<WallpaperState.Success?>(null) }
    if (screenState is WallpaperState.Success) {
        lastSuccess = screenState as WallpaperState.Success
    }

    // True only during a filter reload — i.e. loading but we already have content to show
    val isFilterReloading = screenState is WallpaperState.Loading && lastSuccess != null

    Box(
        Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(WhiteBG)
            .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = screenState) {
            is WallpaperState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    // Keep the grid visible while the new filter results load
                    WallpaperListPage(
                        wallpapers = cached.wallpaperList,
                        selectedTypes = selectedTypes,
                        selectedSeries = selectedSeries,
                        only360 = only360,
                        allSeries = allSeries,
                        allTags = allTags,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMoreInCurrentView = hasMoreInCurrentView,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSeriesToggle = { viewModel.toggleSeries(it) },
                        onToggle360 = { viewModel.toggle360() },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onSearch = { viewModel.searchNow(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                    )
                } else {
                    // Initial load — no cached content yet, show full screen loader
                    LoadingIndicator()
                }
            }

            is WallpaperState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.error,
                onTryAgain = { viewModel.loadAllWallpapers() },
            )

            is WallpaperState.Success -> WallpaperListPage(
                wallpapers = state.wallpaperList,
                selectedTypes = selectedTypes,
                selectedSeries = selectedSeries,
                only360 = only360,
                allSeries = allSeries,
                allTags = allTags,
                searchText = searchText,
                isLoadingMore = isLoadingMore,
                hasMoreInCurrentView = hasMoreInCurrentView,
                onTypeToggle = { viewModel.toggleType(it) },
                onSeriesToggle = { viewModel.toggleSeries(it) },
                onToggle360 = { viewModel.toggle360() },
                onSearchChange = { viewModel.setSearchText(it) },
                onSearch = { viewModel.searchNow(it) },
                onClearFilters = { viewModel.clearAllFilters() },
                onLoadMore = { viewModel.loadMoreIfNeeded() },
            )
        }

        // Overlay is always in the DOM — opacity transitions handle the fade so that
        // both the appear and disappear animations play smoothly via CSS transition.
        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

private const val MASONRY_CARD_VW = 0.25
private const val MASONRY_CARD_MIN = 300.0
private const val MASONRY_CARD_MAX = 480.0
private const val MASONRY_GAP = 16.0
private const val MASONRY_CAPTION_HEIGHT = 44.0

/**
 * packMasonryColumns
 * Splits wallpapers into columns by always placing the next wallpaper in the currently
 * shortest column. Height is estimated from the type's aspect ratio plus a caption allowance.
 * Existing wallpapers never move when more are appended.
 *
 * @param wallpapers Wallpapers in display order.
 * @param columnCount Number of columns to fill.
 * @param cardWidth Column width in px, used to estimate card height.
 * @return One list of wallpapers per column.
 */
private fun packMasonryColumns(
    wallpapers: List<WallpaperResponse>,
    columnCount: Int,
    cardWidth: Double,
): List<List<WallpaperResponse>> {
    val columns = List(columnCount) { mutableListOf<WallpaperResponse>() }
    val heights = DoubleArray(columnCount)
    wallpapers.forEach { wallpaper ->
        val target = heights.indices.minByOrNull { heights[it] } ?: 0
        columns[target].add(wallpaper)
        heights[target] += cardWidth / wallpaperAspectRatio(wallpaper.type) + MASONRY_CAPTION_HEIGHT + 16.0
    }
    return columns
}

@OptIn(DelicateApi::class)
@Composable
private fun WallpaperListPage(
    wallpapers: List<WallpaperResponse>,
    selectedTypes: Set<String>,
    selectedSeries: Set<String>,
    only360: Boolean,
    allSeries: List<String>,
    allTags: List<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMoreInCurrentView: Boolean,
    onTypeToggle: (String) -> Unit,
    onSeriesToggle: (String) -> Unit,
    onToggle360: () -> Unit,
    onSearchChange: (String) -> Unit,
    onSearch: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
) {
    DisposableEffect(hasMoreInCurrentView) {
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMoreInCurrentView && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    var viewportWidth by remember { mutableStateOf(window.innerWidth) }
    DisposableEffect(Unit) {
        val onResize: (org.w3c.dom.events.Event) -> Unit = { viewportWidth = window.innerWidth }
        window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }
    val hasActiveFilters = selectedTypes.isNotEmpty() || selectedSeries.isNotEmpty() || only360 || searchText.isNotBlank()

    Column(
        modifier = if (isMobile)
            Modifier
                .fillMaxWidth()
                .padding(leftRight = 16.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem),
    ) {
        PageHeading(
            subtitle = WallpaperStatics.PAGE_SUBTITLE,
            titleLine1 = WallpaperStatics.PAGE_TITLE_LINE1,
            titleLine2 = WallpaperStatics.PAGE_TITLE_LINE2,
            description = WallpaperStatics.PAGE_DESCRIPTION,
            detail = WallpaperStatics.PAGE_DETAIL_1,
            detailType1 = WallpaperStatics.PAGE_DETAIL_TYPE1,
            detail2 = WallpaperStatics.PAGE_DETAIL_2,
            detailType2 = WallpaperStatics.PAGE_DETAIL_TYPE2,
            detail3 = WallpaperStatics.PAGE_DETAIL_3,
            detailType3 = WallpaperStatics.PAGE_DETAIL_TYPE3,
        )

        Column(
            modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White),
        ) {
            // Search bar row — search input + optional Clear Filters chip at its end
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                Box(modifier = Modifier.flexGrow(1)) {
                    SearchBarWithAutocomplete(
                        value = searchText,
                        onValueChange = onSearchChange,
                        suggestions = allTags,
                        placeholder = WallpaperStatics.SEARCH_PLACEHOLDER,
                        onSearch = onSearch,
                    )
                }

                if (hasActiveFilters) {
                    Row(
                        modifier = PressableButtonStyle.toModifier()
                            .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                            .borderRadius(16.px)
                            .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                            .styleModifier { property("background", "#FFFFFF") }
                            .onClick { onClearFilters() },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = "Clear Filters",
                            modifier = Modifier
                                .fontFamily(FontFamily)
                                .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                                .moxTextGradient(),
                        )
                    }
                }
            }

            // Type filter chips
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 20.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(20.px),
            ) {
                WallpaperStatics.ALL_TYPES.forEach { typeItem ->
                    Chip(
                        description = typeItem,
                        typeChip = true,
                        isSelected = typeItem in selectedTypes,
                        onChipClick = { onTypeToggle(typeItem) },
                    )
                }
                Chip(
                    description = "360°",
                    typeChip = true,
                    isSelected = only360,
                    onChipClick = onToggle360,
                )
            }

            // Series chips — always a single scrollable row with hidden scrollbar.
            if (allSeries.isNotEmpty()) {
                Row(
                    modifier = ChipRowScrollStyle.toModifier()
                        .fillMaxWidth()
                        .padding(top = 12.px, bottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.px),
                ) {
                    allSeries.forEach { seriesItem ->
                        Chip(
                            description = seriesItem,
                            isSelected = seriesItem in selectedSeries,
                            onChipClick = { onSeriesToggle(seriesItem) },
                        )
                    }
                }
            }
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = 30.px)
                .flexGrow(1),
        ) {
            if (isMobile) {
                Row(
                    modifier = Modifier
                        .fillMaxSize()
                        .align(Alignment.TopCenter)
                        .padding(top = 8.px, bottom = 16.px)
                        .flexWrap(FlexWrap.Wrap),
                    horizontalArrangement = Arrangement.spacedBy(16.px),
                ) {
                    wallpapers.forEach { wallpaper ->
                        WallpaperDisplay(wallpaper)
                    }
                }
            } else {
                val cardWidth = (viewportWidth * MASONRY_CARD_VW).coerceIn(MASONRY_CARD_MIN, MASONRY_CARD_MAX)
                val containerInner = (viewportWidth * 0.9).coerceIn(1200.0, 2000.0) - 96.0
                val columnCount = ((containerInner + MASONRY_GAP) / (cardWidth + MASONRY_GAP)).toInt().coerceAtLeast(1)
                val columns = remember(wallpapers, columnCount, cardWidth) {
                    packMasonryColumns(wallpapers, columnCount, cardWidth)
                }
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 8.px, bottom = 16.px),
                    horizontalArrangement = Arrangement.spacedBy(MASONRY_GAP.px, Alignment.CenterHorizontally),
                ) {
                    columns.forEach { column ->
                        Column(modifier = Modifier.width(cardWidth.px)) {
                            column.forEach { wallpaper ->
                                WallpaperDisplay(wallpaper, fillColumn = true)
                            }
                        }
                    }
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) {
                LoadingMoreIndicator()
            }
        }
    }
}
